import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Calendar, Mail, MessageSquare, Phone, Link2, Gift,
  Coffee, Eye, Check, Clock, Bell, Send, Heart, Sparkles,
  Edit2, X, ChevronRight, ArrowRight, Code, Copy, Terminal,
  AlertCircle, CheckCircle, Loader, ChevronDown, ChevronUp
} from 'lucide-react';
import AdminNav from './AdminNav';

interface TreeState {
  recipientName?: string;
  birthMonth?: number;
  birthDay?: number;
  birthdayStatus?: 'belated' | 'today' | 'upcoming';
  scheduleOption?: 'schedule' | 'sendNow';
  deliveryMethod?: 'email' | 'sms' | 'whatsapp' | 'link';
  contactInfo?: string;
  senderName?: string;
  message?: string;
  includesCoffee?: boolean;
  coffeeAmount?: number;
}

interface ApiCall {
  id: string;
  timestamp: Date;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  payload?: any;
  response?: any;
  status: 'pending' | 'success' | 'error';
  duration?: number;
}

const InteractiveDecisionTree: React.FC = () => {
  const [state, setState] = useState<TreeState>({});
  const [editingNode, setEditingNode] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
  const [showApiLog, setShowApiLog] = useState(true);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  const [showStateViewer, setShowStateViewer] = useState(true);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const checkBirthdayStatus = (month: number, day: number): 'belated' | 'today' | 'upcoming' => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const birthday = new Date(currentYear, month - 1, day);

    const daysDiff = Math.floor((birthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) return 'today';
    else if (daysDiff < 0 && daysDiff >= -30) return 'belated';
    else return 'upcoming';
  };

  // Simulate API calls based on state changes
  useEffect(() => {
    if (state.message && state.senderName && state.recipientName && state.deliveryMethod) {
      // Simulate the final send API call
      const cardPayload = {
        recipientName: state.recipientName,
        recipientEmail: state.deliveryMethod === 'email' ? state.contactInfo : `${state.recipientName?.toLowerCase().replace(/\s/g, '')}@birthday.coffee`,
        senderName: state.senderName,
        message: state.message,
        cardDesign: 'confetti-blast',
        includesCoffee: state.includesCoffee || false,
        coffeeAmount: state.coffeeAmount || 0,
        deliveryMethod: state.deliveryMethod,
        birthdayDate: state.birthMonth && state.birthDay ? `${state.birthMonth}/${state.birthDay}` : undefined,
        scheduleDelivery: state.scheduleOption === 'schedule',
        birthdayStatus: state.birthdayStatus
      };

      const newCall: ApiCall = {
        id: `api-${Date.now()}`,
        timestamp: new Date(),
        method: 'POST',
        endpoint: '/api/cards/send',
        payload: cardPayload,
        status: 'pending'
      };

      setApiCalls(prev => [...prev, newCall]);

      // Simulate response after 1 second
      setTimeout(() => {
        setApiCalls(prev => prev.map(call =>
          call.id === newCall.id
            ? {
                ...call,
                status: 'success',
                duration: 342,
                response: {
                  success: true,
                  cardId: 'card_' + Math.random().toString(36).substring(2, 9),
                  message: state.scheduleOption === 'schedule'
                    ? `Scheduled for ${months[(state.birthMonth || 1) - 1]} ${state.birthDay}`
                    : 'Birthday card sent successfully!',
                  trackingUrl: `https://birthday.coffee/track/${Math.random().toString(36).substring(2, 9)}`,
                  giftCode: state.includesCoffee ? `COFFEE-${Math.random().toString(36).substring(2, 9).toUpperCase()}` : null
                }
              }
            : call
        ));
      }, 1000);
    }
  }, [state.message, state.senderName, state.recipientName, state.deliveryMethod]);

  // Add API call for AI message suggestions
  useEffect(() => {
    if (state.recipientName && state.birthdayStatus && state.senderName) {
      const aiCall: ApiCall = {
        id: `ai-${Date.now()}`,
        timestamp: new Date(),
        method: 'POST',
        endpoint: '/api/ai/suggest-message',
        payload: {
          recipientName: state.recipientName,
          senderName: state.senderName,
          birthdayStatus: state.birthdayStatus,
          isScheduled: state.scheduleOption === 'schedule'
        },
        status: 'pending'
      };

      setApiCalls(prev => [...prev, aiCall]);

      setTimeout(() => {
        setApiCalls(prev => prev.map(call =>
          call.id === aiCall.id
            ? {
                ...call,
                status: 'success',
                duration: 523,
                response: {
                  suggestions: [
                    state.birthdayStatus === 'belated'
                      ? "Happy Belated Birthday! 🎂 Sorry I'm late, but my wishes are just as warm!"
                      : "Happy Birthday! 🎉 Wishing you a day filled with joy and laughter!",
                    "Another year older, another year wiser! Have an amazing birthday!",
                    "Hope your special day is as wonderful as you are!"
                  ]
                }
              }
            : call
        ));
      }, 500);
    }
  }, [state.recipientName, state.birthdayStatus, state.senderName]);

  const handleNodeClick = (nodeId: string, currentValue?: any) => {
    setEditingNode(nodeId);
    setTempValue(currentValue?.toString() || '');
  };

  const handleNodeSave = (nodeId: string) => {
    const updates: Partial<TreeState> = {};

    // Log validation API calls for certain fields
    if (nodeId === 'contactInfo' && tempValue) {
      const validationCall: ApiCall = {
        id: `validate-${Date.now()}`,
        timestamp: new Date(),
        method: 'POST',
        endpoint: state.deliveryMethod === 'email' ? '/api/validate/email' : '/api/validate/phone',
        payload: { value: tempValue },
        status: 'success',
        duration: 45,
        response: { valid: true, formatted: tempValue }
      };
      setApiCalls(prev => [...prev, validationCall]);
    }

    switch (nodeId) {
      case 'recipientName':
        updates.recipientName = tempValue;
        break;
      case 'birthday':
        const [month, day] = tempValue.split('/').map(Number);
        if (month && day) {
          updates.birthMonth = month;
          updates.birthDay = day;
          updates.birthdayStatus = checkBirthdayStatus(month, day);
        }
        break;
      case 'scheduleOption':
        updates.scheduleOption = tempValue as 'schedule' | 'sendNow';

        // Log scheduling API call if scheduled
        if (tempValue === 'schedule') {
          const scheduleCall: ApiCall = {
            id: `schedule-${Date.now()}`,
            timestamp: new Date(),
            method: 'POST',
            endpoint: '/api/schedule/check-slot',
            payload: {
              date: `${state.birthMonth}/${state.birthDay}`,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            status: 'success',
            duration: 67,
            response: { available: true, scheduledTime: '09:00 AM' }
          };
          setApiCalls(prev => [...prev, scheduleCall]);
        }
        break;
      case 'deliveryMethod':
        updates.deliveryMethod = tempValue as 'email' | 'sms' | 'whatsapp' | 'link';
        break;
      case 'contactInfo':
        updates.contactInfo = tempValue;
        break;
      case 'senderName':
        updates.senderName = tempValue;
        break;
      case 'message':
        updates.message = tempValue;
        break;
      case 'coffee':
        updates.includesCoffee = tempValue === 'yes';
        if (tempValue === 'yes') {
          updates.coffeeAmount = 10;

          // Log payment setup API call
          const paymentCall: ApiCall = {
            id: `payment-${Date.now()}`,
            timestamp: new Date(),
            method: 'POST',
            endpoint: '/api/payments/create-intent',
            payload: { amount: 1000, currency: 'USD', description: 'Birthday Coffee Gift' },
            status: 'success',
            duration: 234,
            response: {
              intentId: 'pi_' + Math.random().toString(36).substring(2, 15),
              clientSecret: 'sk_test_' + Math.random().toString(36).substring(2, 20)
            }
          };
          setApiCalls(prev => [...prev, paymentCall]);
        }
        break;
    }

    setState({ ...state, ...updates });
    setEditingNode(null);
    setTempValue('');
  };

  const getNodeColor = (nodeId: string) => {
    const hasValue =
      (nodeId === 'recipientName' && state.recipientName) ||
      (nodeId === 'birthday' && state.birthMonth && state.birthDay) ||
      (nodeId === 'deliveryMethod' && state.deliveryMethod) ||
      (nodeId === 'contactInfo' && state.contactInfo) ||
      (nodeId === 'senderName' && state.senderName) ||
      (nodeId === 'message' && state.message) ||
      (nodeId === 'coffee' && state.includesCoffee !== undefined) ||
      (nodeId === 'scheduleOption' && state.scheduleOption);

    return hasValue ? 'from-green-500 to-emerald-600' : 'from-amber-500 to-orange-600';
  };

  const renderNode = (
    nodeId: string,
    title: string,
    subtitle: string,
    value?: string,
    icon?: React.ReactNode,
    indent: number = 0
  ) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`cursor-pointer`}
      style={{ marginLeft: `${indent * 2}rem` }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => handleNodeClick(nodeId, value)}
    >
      <div className={`relative bg-gradient-to-br ${getNodeColor(nodeId)} rounded-2xl p-4 shadow-lg`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-white/90 text-sm font-medium">{title}</div>
          {icon}
        </div>
        <div className="text-white font-semibold text-lg mb-1">{subtitle}</div>
        {value && (
          <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-1 text-white text-sm">
            {value}
          </div>
        )}
        <Edit2 className="absolute top-2 right-2 w-4 h-4 text-white/50" />
      </div>
    </motion.div>
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const clearApiLog = () => {
    setApiCalls([]);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <AdminNav
        title="Interactive Decision Tree"
        description="Interactive decision tree with API logger"
      />
      <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Interactive Flow</h1>
            <p className="text-gray-600">Click any node to set its value and see the flow update!</p>
          </div>

        {/* Status Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">Recipient</div>
              <div className="font-semibold">{state.recipientName || '—'}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">Birthday</div>
              <div className="font-semibold">
                {state.birthMonth && state.birthDay
                  ? `${months[state.birthMonth - 1]} ${state.birthDay}`
                  : '—'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">Delivery</div>
              <div className="font-semibold capitalize">{state.deliveryMethod || '—'}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-1">Status</div>
              <div className="font-semibold capitalize">
                {state.birthdayStatus ? (
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    state.birthdayStatus === 'today' ? 'bg-green-100 text-green-700' :
                    state.birthdayStatus === 'belated' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {state.birthdayStatus}
                  </span>
                ) : '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Split View Container */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Interactive Tree - Vertical Flow */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-xl p-8">
          <div className="space-y-4">
            {/* Step 1: Recipient */}
            {renderNode(
              'recipientName',
              'Step 1',
              "Who's Birthday?",
              state.recipientName,
              <User className="w-5 h-5 text-white" />,
              0
            )}

            {/* Connector Line */}
            {state.recipientName && (
              <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
            )}

            {/* Step 2: Birthday Date */}
            {state.recipientName && renderNode(
              'birthday',
              'Step 2',
              "When's Birthday?",
              state.birthMonth && state.birthDay ? `${months[state.birthMonth - 1]} ${state.birthDay}` : undefined,
              <Calendar className="w-5 h-5 text-white" />,
              0
            )}

            {/* Birthday Status Branch */}
            {state.birthMonth && state.birthDay && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                <div className="ml-4">
                  {state.birthdayStatus === 'belated' && (
                    <div className="inline-block bg-yellow-100 text-yellow-800 rounded-xl px-4 py-2 font-semibold">
                      🎂 Belated Birthday (Past 30 days)
                    </div>
                  )}
                  {state.birthdayStatus === 'today' && (
                    <div className="inline-block bg-green-100 text-green-800 rounded-xl px-4 py-2 font-semibold">
                      🎉 Today's Birthday!
                    </div>
                  )}
                  {state.birthdayStatus === 'upcoming' && (
                    <>
                      <div className="inline-block bg-purple-100 text-purple-800 rounded-xl px-4 py-2 font-semibold mb-4">
                        📅 Upcoming Birthday
                      </div>
                      {renderNode(
                        'scheduleOption',
                        'Scheduling',
                        'When to send?',
                        state.scheduleOption === 'schedule' ? 'Schedule for birthday' :
                        state.scheduleOption === 'sendNow' ? 'Send now' : undefined,
                        <Clock className="w-5 h-5 text-white" />,
                        1
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {/* Step 3: Delivery Method */}
            {state.birthdayStatus && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                {renderNode(
                  'deliveryMethod',
                  'Step 3',
                  'Delivery Method',
                  state.deliveryMethod ? (
                    state.deliveryMethod === 'sms' ? 'SMS' :
                    state.deliveryMethod === 'link' ? 'Share Link' :
                    state.deliveryMethod.charAt(0).toUpperCase() + state.deliveryMethod.slice(1)
                  ) : undefined,
                  <Send className="w-5 h-5 text-white" />,
                  0
                )}
              </>
            )}

            {/* Step 4: Contact Info (conditional) */}
            {state.deliveryMethod && state.deliveryMethod !== 'link' && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                {renderNode(
                  'contactInfo',
                  'Step 4',
                  state.deliveryMethod === 'email' ? 'Email Address' : 'Phone Number',
                  state.contactInfo,
                  state.deliveryMethod === 'email' ? <Mail className="w-5 h-5 text-white" /> : <Phone className="w-5 h-5 text-white" />,
                  1
                )}
              </>
            )}

            {/* Step 5: Sender Name */}
            {((state.deliveryMethod === 'link') || state.contactInfo) && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                {renderNode(
                  'senderName',
                  state.deliveryMethod === 'link' ? 'Step 4' : 'Step 5',
                  'Your Name',
                  state.senderName,
                  <Heart className="w-5 h-5 text-white" />,
                  0
                )}
              </>
            )}

            {/* Step 6: Message */}
            {state.senderName && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                {renderNode(
                  'message',
                  state.deliveryMethod === 'link' ? 'Step 5' : 'Step 6',
                  'Birthday Message',
                  state.message ? `${state.message.substring(0, 50)}...` : undefined,
                  <Sparkles className="w-5 h-5 text-white" />,
                  0
                )}
              </>
            )}

            {/* Step 7: Coffee Gift */}
            {state.message && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                {renderNode(
                  'coffee',
                  state.deliveryMethod === 'link' ? 'Step 6' : 'Step 7',
                  'Add Coffee Gift?',
                  state.includesCoffee !== undefined ? (state.includesCoffee ? 'Yes ($10)' : 'No') : undefined,
                  <Coffee className="w-5 h-5 text-white" />,
                  0
                )}
              </>
            )}

            {/* Final Status */}
            {state.message && state.senderName && (
              <>
                <div className="ml-8 border-l-2 border-gray-300 h-4"></div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-2xl p-6 shadow-xl"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-5 h-5" />
                    <span className="font-semibold text-lg">Ready to Send!</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-white/70">To:</div>
                      <div className="font-medium">{state.recipientName}</div>
                    </div>
                    <div>
                      <div className="text-white/70">From:</div>
                      <div className="font-medium">{state.senderName}</div>
                    </div>
                    <div>
                      <div className="text-white/70">Method:</div>
                      <div className="font-medium capitalize">{state.deliveryMethod || 'Not selected'}</div>
                    </div>
                    <div>
                      <div className="text-white/70">When:</div>
                      <div className="font-medium">
                        {state.birthdayStatus === 'today' ? 'Today' :
                         state.birthdayStatus === 'belated' ? 'Belated' :
                         state.scheduleOption === 'schedule' ? 'Scheduled' : 'Now'}
                      </div>
                    </div>
                    {state.includesCoffee && (
                      <div className="col-span-2">
                        <div className="text-white/70">Gift:</div>
                        <div className="font-medium">$10 Coffee ☕</div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </>
            )}
            </div>
          </div>

          {/* React State Viewer */}
          <div className="bg-gray-900 rounded-3xl shadow-xl overflow-hidden">
            <div className="p-4 bg-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-blue-400" />
                <span className="text-white font-semibold text-sm">React State</span>
              </div>
              <button
                onClick={() => setShowStateViewer(!showStateViewer)}
                className="p-1 text-gray-400 hover:text-white transition-colors"
              >
                {showStateViewer ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            <AnimatePresence>
              {showStateViewer && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                    {/* useState Hook Display */}
                    <div className="bg-gray-800 rounded-lg p-3">
                      <div className="text-blue-400 font-mono text-xs mb-2">// React Component State</div>
                      <pre className="text-gray-300 font-mono text-xs">
                        <span className="text-purple-400">const</span> [state, setState] = <span className="text-cyan-400">useState</span>&lt;<span className="text-green-400">TreeState</span>&gt;({`{
${Object.entries(state).map(([key, value]) =>
  `  ${key}: ${typeof value === 'string' ? `"${value}"` : value}`
).join(',\n')}
}`})
                      </pre>
                    </div>

                    {/* Current State Object */}
                    <div className="bg-gray-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-green-400 font-mono text-xs">// Current State Object</div>
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(state, null, 2))}
                          className="text-gray-500 hover:text-white transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <pre className="text-gray-300 font-mono text-xs overflow-x-auto">
                        {JSON.stringify(state, null, 2)}
                      </pre>
                    </div>

                    {/* State Update Log */}
                    <div className="bg-gray-800 rounded-lg p-3">
                      <div className="text-yellow-400 font-mono text-xs mb-2">// Last State Update</div>
                      {editingNode ? (
                        <div className="text-gray-400 font-mono text-xs">
                          <span className="text-orange-400">Editing:</span> {editingNode}
                          <br />
                          <span className="text-orange-400">Temp Value:</span> "{tempValue}"
                        </div>
                      ) : (
                        <div className="text-gray-500 font-mono text-xs">No active edits</div>
                      )}
                    </div>

                    {/* Hook Usage Example */}
                    <div className="bg-gray-800 rounded-lg p-3">
                      <div className="text-cyan-400 font-mono text-xs mb-2">// Example: Update Handler</div>
                      <pre className="text-gray-300 font-mono text-xs">{`const handleNodeSave = (nodeId: string) => {
  const updates: Partial<TreeState> = {};

  // Update specific field
  updates[nodeId] = tempValue;

  // Merge with existing state
  setState({ ...state, ...updates });
};`}
                      </pre>
                    </div>

                    {/* State Dependencies */}
                    {state.birthdayStatus && (
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-pink-400 font-mono text-xs mb-2">// Computed Values</div>
                        <div className="text-gray-300 font-mono text-xs space-y-1">
                          <div><span className="text-gray-500">birthdayStatus:</span> {state.birthdayStatus}</div>
                          <div><span className="text-gray-500">isComplete:</span> {state.message && state.senderName ? 'true' : 'false'}</div>
                          <div><span className="text-gray-500">totalAmount:</span> ${state.includesCoffee ? '10.00' : '0.00'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* API Call Logger */}
        <div className="bg-gray-900 rounded-3xl shadow-xl overflow-hidden">
          <div className="p-4 bg-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-5 h-5 text-green-400" />
              <span className="text-white font-semibold">API Call Logger</span>
              <span className="text-gray-400 text-sm">({apiCalls.length} calls)</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearApiLog}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
              >
                Clear Log
              </button>
              <button
                onClick={() => setShowApiLog(!showApiLog)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                {showApiLog ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {showApiLog && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="max-h-96 overflow-y-auto p-4 space-y-3">
                  {apiCalls.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">
                      <Code className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No API calls yet. Start filling out the form above!</p>
                    </div>
                  ) : (
                    apiCalls.map((call) => (
                      <motion.div
                        key={call.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-gray-800 rounded-lg p-3 border border-gray-700"
                      >
                        <div
                          className="flex items-start justify-between cursor-pointer"
                          onClick={() => setExpandedCall(expandedCall === call.id ? null : call.id)}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`mt-1 w-2 h-2 rounded-full ${
                              call.status === 'success' ? 'bg-green-400' :
                              call.status === 'error' ? 'bg-red-400' :
                              'bg-yellow-400 animate-pulse'
                            }`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                  call.method === 'GET' ? 'bg-blue-600 text-white' :
                                  call.method === 'POST' ? 'bg-green-600 text-white' :
                                  call.method === 'PUT' ? 'bg-yellow-600 text-white' :
                                  'bg-red-600 text-white'
                                }`}>
                                  {call.method}
                                </span>
                                <span className="text-white font-mono text-sm">{call.endpoint}</span>
                                {call.duration && (
                                  <span className="text-gray-500 text-xs ml-auto">{call.duration}ms</span>
                                )}
                              </div>
                              <div className="text-gray-400 text-xs">
                                {call.timestamp.toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${
                            expandedCall === call.id ? 'rotate-180' : ''
                          }`} />
                        </div>

                        <AnimatePresence>
                          {expandedCall === call.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="mt-3 pt-3 border-t border-gray-700"
                            >
                              {call.payload && (
                                <div className="mb-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-gray-400 text-xs uppercase">Request Payload</span>
                                    <button
                                      onClick={() => copyToClipboard(JSON.stringify(call.payload, null, 2))}
                                      className="text-gray-500 hover:text-white transition-colors"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <pre className="bg-gray-900 rounded p-2 text-xs text-gray-300 overflow-x-auto">
                                    {JSON.stringify(call.payload, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {call.response && (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-gray-400 text-xs uppercase">Response</span>
                                    <button
                                      onClick={() => copyToClipboard(JSON.stringify(call.response, null, 2))}
                                      className="text-gray-500 hover:text-white transition-colors"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <pre className="bg-gray-900 rounded p-2 text-xs text-gray-300 overflow-x-auto">
                                    {JSON.stringify(call.response, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-white/80 backdrop-blur rounded-2xl p-6">
          <h3 className="font-semibold text-gray-800 mb-3">How to use:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold shrink-0">1</div>
              <span>Click any orange node to edit its value</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold shrink-0">2</div>
              <span>Green nodes indicate completed fields</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold shrink-0">3</div>
              <span>Watch API calls appear in the logger below</span>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-bold shrink-0">4</div>
              <span>Click on API calls to see full payloads and responses</span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingNode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setEditingNode(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">
                  {editingNode === 'recipientName' && "Who's Birthday?"}
                  {editingNode === 'birthday' && "When's Their Birthday?"}
                  {editingNode === 'scheduleOption' && "When to Send?"}
                  {editingNode === 'deliveryMethod' && "How to Deliver?"}
                  {editingNode === 'contactInfo' && "Contact Information"}
                  {editingNode === 'senderName' && "Your Name"}
                  {editingNode === 'message' && "Birthday Message"}
                  {editingNode === 'coffee' && "Add Coffee Gift?"}
                </h3>
                <button
                  onClick={() => setEditingNode(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {editingNode === 'recipientName' && (
                <input
                  type="text"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder="Enter recipient's name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
                  autoFocus
                />
              )}

              {editingNode === 'birthday' && (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    placeholder="MM/DD (e.g., 3/15)"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
                    autoFocus
                  />
                  <p className="text-sm text-gray-500">Format: Month/Day (e.g., 12/25)</p>
                </div>
              )}

              {editingNode === 'scheduleOption' && (
                <div className="space-y-2">
                  <button
                    onClick={() => setTempValue('schedule')}
                    className={`w-full py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'schedule' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Bell className="w-5 h-5" />
                      <span>Schedule for Birthday</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setTempValue('sendNow')}
                    className={`w-full py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'sendNow' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      <span>Send Now</span>
                    </div>
                  </button>
                </div>
              )}

              {editingNode === 'deliveryMethod' && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setTempValue('email')}
                    className={`py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'email' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Mail className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Email</span>
                  </button>
                  <button
                    onClick={() => setTempValue('sms')}
                    className={`py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'sms' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <MessageSquare className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">SMS</span>
                  </button>
                  <button
                    onClick={() => setTempValue('whatsapp')}
                    className={`py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'whatsapp' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Phone className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => setTempValue('link')}
                    className={`py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'link' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Link2 className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm">Share Link</span>
                  </button>
                </div>
              )}

              {editingNode === 'contactInfo' && (
                <input
                  type={state.deliveryMethod === 'email' ? 'email' : 'tel'}
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder={state.deliveryMethod === 'email' ? 'email@example.com' : '+1 234 567 8900'}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
                  autoFocus
                />
              )}

              {editingNode === 'senderName' && (
                <input
                  type="text"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500"
                  autoFocus
                />
              )}

              {editingNode === 'message' && (
                <textarea
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  placeholder="Write your birthday message..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 h-32 resize-none"
                  autoFocus
                />
              )}

              {editingNode === 'coffee' && (
                <div className="space-y-2">
                  <button
                    onClick={() => setTempValue('yes')}
                    className={`w-full py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'yes' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Coffee className="w-5 h-5 text-amber-600" />
                      <span>Yes, add $10 coffee gift</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setTempValue('no')}
                    className={`w-full py-3 rounded-lg border-2 transition-colors ${
                      tempValue === 'no' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    No thanks
                  </button>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleNodeSave(editingNode)}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition-shadow"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingNode(null)}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-lg font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default InteractiveDecisionTree;